/**
 * Tracks user input modality and reflects it on `<html data-modality="...">`.
 *
 * Browsers match `:focus-visible` on text inputs even when focus is moved by a
 * mouse click (per spec) — clicking an input implies upcoming keyboard use.
 * That causes our focus ring to appear on click as well as on Tab, which is
 * not the behavior we want. Gating the focus-ring rule on
 * `html[data-modality="keyboard"]` makes it appear only when the most recent
 * interaction was keyboard.
 */
let installed = false

export function installModalityTracker() {
  if (installed || typeof document === 'undefined') return
  installed = true

  const setModality = (modality: 'keyboard' | 'pointer') => {
    if (document.documentElement.getAttribute('data-modality') !== modality) {
      document.documentElement.setAttribute('data-modality', modality)
    }
  }

  // Only keys that move focus flip the modality to keyboard. A11y users rely
  // on Tab/Shift+Tab and arrow keys for menus/radios; plain typing into an
  // input shouldn't be treated as keyboard navigation.
  const onKeyDown = (e: KeyboardEvent) => {
    if (
      e.key === 'Tab' ||
      e.key === 'ArrowUp' ||
      e.key === 'ArrowDown' ||
      e.key === 'ArrowLeft' ||
      e.key === 'ArrowRight'
    ) {
      setModality('keyboard')
    }
  }

  const onPointer = () => setModality('pointer')

  document.addEventListener('keydown', onKeyDown, true)
  document.addEventListener('mousedown', onPointer, true)
  document.addEventListener('touchstart', onPointer, true)
}
